import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { TrendingUp, TrendingDown, Activity, AlertTriangle, Wind, Sun, Thermometer, X, ChevronDown, MapPin, Layers } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { RegionSelector } from '../components/workspace/RegionSelector';
import { TableModelDropdown } from '../components/workspace/TableModelDropdown';
// --- Types ---
type TrafficState = 'green' | 'yellow' | 'red';
interface NewsItem {
  id: string;
  time: string;
  text: string;
  priority: 'high' | 'medium' | 'low';
}
interface ModelData {
  time: string;
  ept2: number;
  ecifs: number;
  gfs: number;
  actual?: number;
}
// --- Model definitions ---
const ALL_MODELS = [{
  name: 'EPT-2',
  category: 'ai' as const
}, {
  name: 'EPT-2 Early',
  category: 'ai' as const
}, {
  name: 'EC AIFS',
  category: 'ai' as const
}, {
  name: 'EC IFS',
  category: 'nwp' as const
}, {
  name: 'GFS',
  category: 'nwp' as const
}, {
  name: 'ICON-EU',
  category: 'nwp' as const
}, {
  name: 'UKMO',
  category: 'nwp' as const
}];
const generateAvailableRuns = () => {
  const runs = [];
  const now = new Date();
  for (let d = 0; d < 2; d++) {
    for (const hz of [0, 6, 12, 18]) {
      const date = new Date(now);
      date.setDate(date.getDate() - d);
      date.setUTCHours(hz, 0, 0, 0);
      if (date <= now) {
        runs.push({
          label: `${date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short'
          })} ${hz.toString().padStart(2, '0')}z`,
          value: date.toISOString(),
          date
        });
      }
    }
  }
  return runs.sort((a, b) => b.date.getTime() - a.date.getTime());
};
/** Returns the two most recent 6-hourly EPT-2 run ISO strings */
const getLatestTwoEpt2Runs = (): string[] => {
  const runs = generateAvailableRuns();
  return runs.slice(0, 2).map(r => r.value);
};
// --- Mock Data Generators ---
const generateIntradayData = (hours = 24): ModelData[] => {
  const data: ModelData[] = [];
  const now = new Date();
  now.setMinutes(0, 0, 0);
  for (let i = 0; i < hours; i++) {
    const time = new Date(now.getTime() + i * 3600000);
    const baseVal = 50 + Math.sin(i / 4) * 20;
    data.push({
      time: time.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      ept2: baseVal + Math.random() * 5 - 2.5,
      ecifs: baseVal + Math.random() * 8 - 4,
      gfs: baseVal + Math.random() * 10 - 5,
      actual: i < 6 ? baseVal + Math.random() * 2 - 1 : undefined
    });
  }
  return data;
};
const NEWS_ITEMS: NewsItem[] = [{
  id: '1',
  time: '10:42',
  text: 'German wind output deviation > 2GW vs forecast — North Sea conditions deteriorating',
  priority: 'high'
}, {
  id: '2',
  time: '10:38',
  text: 'EDF: Flamanville 3 EPR unplanned outage — 1,650MW offline, return date TBC',
  priority: 'high'
}, {
  id: '3',
  time: '10:31',
  text: 'RTE outage bulletin: Paluel 2 (1,330MW) maintenance extended by 48h to 22 Feb',
  priority: 'high'
}, {
  id: '4',
  time: '10:28',
  text: 'DE-FR interconnector NTC reduced to 2,100MW (from 3,100MW) — grid congestion',
  priority: 'high'
}, {
  id: '5',
  time: '10:15',
  text: 'Spot price spike in NL market: €187/MWh — interconnection constraints persist',
  priority: 'high'
}, {
  id: '6',
  time: '10:09',
  text: 'Vattenfall: Ringhals 3 (1,063MW) back online after 6h unplanned outage',
  priority: 'medium'
}, {
  id: '7',
  time: '10:02',
  text: 'French nuclear availability updated: +1.2GW — Bugey 4 returned to service',
  priority: 'medium'
}, {
  id: '8',
  time: '09:55',
  text: 'UK demand tracking 5% below seasonal normal — mild temperatures across England',
  priority: 'medium'
}, {
  id: '9',
  time: '09:47',
  text: 'NO2-DE flow at 94% NTC utilisation — hydro surplus driving export pressure',
  priority: 'medium'
}, {
  id: '10',
  time: '09:33',
  text: 'Updated cloud cover forecast for Southern Italy — solar CF revised down 8% D+1',
  priority: 'low'
}, {
  id: '11',
  time: '09:21',
  text: 'Tennet: Borssele offshore cluster curtailment lifted — 1,500MW wind back online',
  priority: 'medium'
}, {
  id: '12',
  time: '09:10',
  text: 'EPEX DA auction cleared DE at €94.20/MWh — 12% above 7-day average',
  priority: 'medium'
}, {
  id: '13',
  time: '08:58',
  text: 'Elia: Belgian grid frequency event 09:54 UTC — aFRR activated 320MW, resolved',
  priority: 'high'
}, {
  id: '14',
  time: '08:45',
  text: 'GFS 06z run shows significant wind downgrade D+2 Central Europe vs 00z — spread widening',
  priority: 'high'
}, {
  id: '15',
  time: '08:30',
  text: 'Gasunie: Groningen field output reduced 15% — gas-to-power impact expected peak hours',
  priority: 'medium'
}];
// --- Components ---
const NewsTicker = () => {
  return <div className="w-full bg-[#EC4899]/10 border-b border-[#EC4899]/20 h-8 flex items-center overflow-hidden relative flex-shrink-0" data-id="element-2382">
      <div className="bg-[#EC4899] px-3 h-full flex items-center z-10 flex-shrink-0" data-id="element-2383">
        <span className="text-[10px] font-bold text-white uppercase tracking-wider" data-id="element-2384">
          Live News
        </span>
      </div>
      <div className="flex-1 overflow-hidden flex items-center h-full" data-id="element-2385">
        <div className="animate-marquee items-center gap-8" style={{
        display: 'flex',
        alignItems: 'center'
      }} data-id="element-2386">
          {NEWS_ITEMS.map(item => <div key={item.id} className="flex items-center gap-2 text-xs flex-shrink-0 px-2" data-id="element-2387">
              <span className="text-slate-400 font-mono" data-id="element-2388">{item.time}</span>
              <span className="text-slate-500 mx-1" data-id="element-2389">·</span>
              <span className={item.priority === 'high' ? 'text-white font-medium' : 'text-slate-300'} data-id="element-2390">
                {item.text}
              </span>
              {item.priority === 'high' && <AlertTriangle size={10} className="text-amber-400 flex-shrink-0 ml-1" data-id="element-2391" />}
            </div>)}
          {NEWS_ITEMS.map(item => <div key={`dup-${item.id}`} className="flex items-center gap-2 text-xs flex-shrink-0 px-2" data-id="element-2392">
              <span className="text-slate-400 font-mono" data-id="element-2393">{item.time}</span>
              <span className="text-slate-500 mx-1" data-id="element-2394">·</span>
              <span className={item.priority === 'high' ? 'text-white font-medium' : 'text-slate-300'} data-id="element-2395">
                {item.text}
              </span>
              {item.priority === 'high' && <AlertTriangle size={10} className="text-amber-400 flex-shrink-0 ml-1" data-id="element-2396" />}
            </div>)}
        </div>
      </div>
    </div>;
};
const TrafficLight = ({
  label,
  state,
  value,
  unit,
  trend,
  delta,
  compareModel,
  compareRun
}: {
  label: string;
  state: TrafficState;
  value: string;
  unit: string;
  trend: 'up' | 'down' | 'flat';
  delta: string;
  compareModel: string;
  compareRun: string;
}) => {
  const colors = {
    green: 'bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.4)]',
    yellow: 'bg-amber-400 shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    red: 'bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.4)]'
  };
  const borderColors = {
    green: 'border-emerald-500/30 bg-emerald-500/5',
    yellow: 'border-amber-400/30 bg-amber-400/5',
    red: 'border-red-500/30 bg-red-500/5'
  };
  return <div className={`flex items-center justify-between p-4 rounded-xl border ${borderColors[state]} transition-all duration-500`} data-id="element-2397">
      <div className="flex items-center gap-4" data-id="element-2398">
        <div className={`w-4 h-4 rounded-full ${colors[state]} transition-all duration-500`} data-id="element-2399" />
        <div data-id="element-2400">
          <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-0.5" data-id="element-2401">
            {label}
          </div>
          <div className="flex items-baseline gap-1.5" data-id="element-2402">
            <span className="text-2xl font-bold text-white tabular-nums" data-id="element-2403">
              {value}
            </span>
            <span className="text-sm text-slate-500 font-medium" data-id="element-2404">{unit}</span>
          </div>
        </div>
      </div>
      <div className={`flex flex-col items-end gap-0.5`} data-id="element-2405">
        <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-slate-400'}`} data-id="element-2406">
          {trend === 'up' ? <TrendingUp size={16} data-id="element-2407" /> : trend === 'down' ? <TrendingDown size={16} data-id="element-2408" /> : <Activity size={16} data-id="element-2409" />}
          <span data-id="element-2410">{delta}</span>
        </div>
        <div className="text-[10px] text-slate-500 text-right leading-tight" data-id="element-2411">
          vs <span className="text-slate-400 font-medium" data-id="element-2412">{compareModel}</span>{' '}
          <span className="font-mono text-slate-500" data-id="element-2413">{compareRun}</span>
        </div>
      </div>
    </div>;
};
const MarketSummary = () => {
  return null;
};
const DeviationHeatmap = ({
  selectedModelRuns
}: {
  selectedModelRuns: Record<string, string[]>;
}) => {
  const hours = Array.from({
    length: 24
  }, (_, i) => i);
  // Build rows: one per model+run combo, same as TableWidget entries
  const rows = useMemo(() => {
    const result: {
      modelName: string;
      runIso: string;
      label: string;
      subLabel: string;
    }[] = [];
    Object.entries(selectedModelRuns).forEach(([modelName, runs]) => {
      if (!runs || runs.length === 0) return;
      const multipleRuns = runs.length > 1;
      runs.forEach(runIso => {
        let subLabel = '';
        if (runIso) {
          const d = new Date(runIso);
          subLabel = d.getUTCHours().toString().padStart(2, '0') + 'z';
        }
        result.push({
          modelName,
          runIso,
          label: multipleRuns && runIso ? `${modelName} (${subLabel})` : modelName,
          subLabel
        });
      });
    });
    // Fall back to defaults if nothing selected
    if (result.length === 0) {
      return [{
        modelName: 'EPT-2',
        runIso: '',
        label: 'EPT-2',
        subLabel: ''
      }, {
        modelName: 'EC IFS',
        runIso: '',
        label: 'EC IFS',
        subLabel: ''
      }, {
        modelName: 'GFS',
        runIso: '',
        label: 'GFS',
        subLabel: ''
      }];
    }
    return result;
  }, [selectedModelRuns]);
  // Stable deviation values per row+hour (seeded by model+run so they don't re-randomize)
  const deviationData = useMemo(() => {
    return rows.map(row => {
      const seed = (row.modelName + row.runIso).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) * 137;
      let s = seed;
      const rand = () => {
        s = s * 16807 % 2147483647;
        return (s - 1) / 2147483646;
      };
      return hours.map(h => +((rand() * 6 - 3) * (h > 12 ? 1.5 : 0.5)).toFixed(2));
    });
  }, [rows]);
  const getCellColor = (val: number) => {
    const abs = Math.abs(val);
    if (abs < 0.5) return 'bg-slate-700/40 text-slate-600';
    if (abs < 1.5) return 'bg-blue-500/25 text-blue-400';
    if (abs < 3.0) return 'bg-amber-500/30 text-amber-400';
    return 'bg-red-500/35 text-red-400 font-bold';
  };
  return <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 overflow-x-auto" data-id="element-2414">
      <div className="flex items-center justify-between mb-4" data-id="element-2415">
        <div data-id="element-2416">
          <h3 className="text-sm font-semibold text-white" data-id="element-2417">
            Model Deviation Heatmap (GW)
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-2418">
            {rows.length} model run{rows.length !== 1 ? 's' : ''} · vs
            climatological normal
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500" data-id="element-2419">
          <span className="flex items-center gap-1" data-id="element-2420">
            <div className="w-2 h-2 bg-slate-800/50 rounded-sm" data-id="element-2421" /> &lt;0.5
          </span>
          <span className="flex items-center gap-1" data-id="element-2422">
            <div className="w-2 h-2 bg-blue-500/20 rounded-sm" data-id="element-2423" /> 0.5–1.5
          </span>
          <span className="flex items-center gap-1" data-id="element-2424">
            <div className="w-2 h-2 bg-amber-500/20 rounded-sm" data-id="element-2425" /> 1.5–3.0
          </span>
          <span className="flex items-center gap-1" data-id="element-2426">
            <div className="w-2 h-2 bg-red-500/20 rounded-sm" data-id="element-2427" /> &gt;3.0
          </span>
        </div>
      </div>

      <div className="min-w-[640px]" data-id="element-2428">
        {/* Hour headers */}
        <div className="grid gap-1 mb-1" style={{
        gridTemplateColumns: '100px repeat(24, 1fr)'
      }} data-id="element-2429">
          <div className="text-[10px] font-medium text-slate-500" data-id="element-2430">
            Model / Hour
          </div>
          {hours.map(h => <div key={h} className="text-[10px] text-center text-slate-500" data-id="element-2431">
              {h}
            </div>)}
        </div>

        {/* Rows */}
        <div className="space-y-1" data-id="element-2432">
          {rows.map((row, rowIdx) => <div key={`${row.modelName}-${row.runIso}-${rowIdx}`} className="grid gap-1" style={{
          gridTemplateColumns: '100px repeat(24, 1fr)'
        }} data-id="element-2433">
              {/* Row label */}
              <div className="flex flex-col justify-center min-w-0 pr-1" data-id="element-2434">
                <span className="text-xs font-medium text-slate-300 truncate" data-id="element-2435">
                  {row.label}
                </span>
                {row.subLabel && !row.label.includes(row.subLabel) && <span className="text-[9px] font-mono text-slate-500" data-id="element-2436">
                    {row.subLabel}
                  </span>}
              </div>
              {/* Cells */}
              {deviationData[rowIdx].map((val, h) => {
            const abs = Math.abs(val);
            const textClass = abs < 0.5 ? 'text-slate-600' : abs < 1.5 ? 'text-blue-400' : abs < 3.0 ? 'text-amber-400' : 'text-red-400 font-bold';
            return <div key={h} className={`h-6 rounded-sm flex items-center justify-center text-[9px] ${getCellColor(val)}`} title={`${row.label} Hour ${h}: ${val.toFixed(2)} GW`} data-id="element-2437">
                    <span className={textClass} data-id="element-2438">
                      {val > 0 ? '+' : ''}
                      {val.toFixed(1)}
                    </span>
                  </div>;
          })}
            </div>)}
        </div>
      </div>
    </div>;
};
// --- Main View ---
export function IntradayTraderView() {
  const [selectedRegion, setSelectedRegion] = useState('Germany');
  const [regionSelectorOpen, setRegionSelectorOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [selectedModelRuns, setSelectedModelRuns] = useState<Record<string, string[]>>(() => {
    const ept2Runs = getLatestTwoEpt2Runs();
    return {
      'EPT-2': ept2Runs
    };
  });
  const regionButtonRef = useRef<HTMLButtonElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);
  const availableRuns = useMemo(() => generateAvailableRuns(), []);
  const activeModelCount = Object.values(selectedModelRuns).filter(r => r.length > 0).length;
  const handleToggleModel = (name: string) => {
    setSelectedModelRuns(prev => {
      const current = prev[name] || [];
      if (current.length > 0) {
        const next = {
          ...prev
        };
        delete next[name];
        return next;
      }
      return {
        ...prev,
        [name]: ['']
      };
    });
  };
  const handleToggleRun = (modelName: string, runValue: string) => {
    setSelectedModelRuns(prev => {
      const current = prev[modelName] || [];
      const hasRun = current.includes(runValue);
      return {
        ...prev,
        [modelName]: hasRun ? current.filter(r => r !== runValue) : [...current.filter(r => r !== ''), runValue]
      };
    });
  };
  const windData = useMemo(() => generateIntradayData(24), []);
  const solarData = useMemo(() => generateIntradayData(24), []);
  const tempData = useMemo(() => generateIntradayData(24), []);
  return <div className="jua-page-bg min-h-screen text-slate-100 flex flex-col" data-id="element-2439">
      <NewsTicker data-id="element-2440" />

      <div className="flex-1 p-6 overflow-y-auto" data-id="element-2441">
        <div className="max-w-[1600px] mx-auto space-y-5" data-id="element-2442">
          {/* Toolbar: Region + Model selectors inline (like workspaces) */}
          <div className="flex items-center gap-3 flex-wrap" data-id="element-2443">
            {/* Region button */}
            <button ref={regionButtonRef} onClick={() => setRegionSelectorOpen(!regionSelectorOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/60 hover:border-slate-600 text-sm text-white transition-colors" data-id="element-2444">
              <MapPin size={13} className="text-[#EC4899]" data-id="element-2445" />
              <span className="font-medium" data-id="element-2446">{selectedRegion}</span>
              <ChevronDown size={13} className="text-slate-500" data-id="element-2447" />
            </button>

            {regionSelectorOpen && <RegionSelector value={selectedRegion} onChange={region => setSelectedRegion(region)} onClose={() => setRegionSelectorOpen(false)} style={{
            top: regionButtonRef.current ? regionButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: regionButtonRef.current ? regionButtonRef.current.getBoundingClientRect().left : 0,
            zIndex: 9999
          }} data-id="element-2448" />}

            {/* Divider */}
            <div className="w-px h-5 bg-slate-700/60" data-id="element-2449" />

            {/* Model button */}
            <button ref={modelButtonRef} onClick={() => setModelDropdownOpen(!modelDropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/60 hover:border-slate-600 text-sm text-white transition-colors" data-id="element-2450">
              <Layers size={13} className="text-[#EC4899]" data-id="element-2451" />
              <span className="font-medium" data-id="element-2452">
                {activeModelCount > 0 ? `${activeModelCount} model${activeModelCount > 1 ? 's' : ''}` : 'Select models'}
              </span>
              <ChevronDown size={13} className={`text-slate-500 transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2453" />
            </button>

            {/* Model pills */}
            {activeModelCount > 0 && <div className="flex flex-wrap gap-1" data-id="element-2454">
                {Object.entries(selectedModelRuns).filter(([, runs]) => runs.length > 0).flatMap(([name, runs]) => runs.map(runIso => {
              const label = runIso ? ` ${new Date(runIso).getUTCHours().toString().padStart(2, '0')}z` : '';
              const key = runIso ? `${name}__${runIso}` : name;
              return <span key={key} className="text-[10px] px-2 py-0.5 rounded-full bg-[#EC4899]/10 border border-[#EC4899]/20 text-[#EC4899] font-medium font-mono" data-id="element-2455">
                          {name}
                          {label}
                        </span>;
            }))}
              </div>}

            {modelDropdownOpen && modelButtonRef.current && <TableModelDropdown allModels={ALL_MODELS} selectedModelRuns={selectedModelRuns} onToggleModel={handleToggleModel} onToggleRun={handleToggleRun} onClearAll={() => setSelectedModelRuns({})} availableRuns={availableRuns} style={{
            top: modelButtonRef.current.getBoundingClientRect().bottom + 8,
            left: modelButtonRef.current.getBoundingClientRect().left,
            zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)'
          }} data-id="element-2456" />}
          </div>

          {/* Traffic Lights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2457">
            <TrafficLight label="Solar State" state="green" value="24.5" unit="GW" trend="up" delta="+1.2 GW" compareModel="EPT-2" compareRun="06z" data-id="element-2458" />
            <TrafficLight label="Wind State" state="red" value="12.8" unit="GW" trend="down" delta="-2.1 GW" compareModel="EC IFS" compareRun="12z" data-id="element-2459" />
            <TrafficLight label="Demand State" state="yellow" value="68.2" unit="GW" trend="up" delta="+1.5 GW" compareModel="EPT-2" compareRun="12z" data-id="element-2460" />
          </div>

          {/* Charts: Summary left, sparklines right */}
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" data-id="element-2461">
            {/* Market Summary */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex flex-col" data-id="element-2462">
              <div className="flex items-center gap-2 mb-3" data-id="element-2463">
                <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] animate-pulse" data-id="element-2464" />
                <h3 className="text-sm font-semibold text-white" data-id="element-2465">
                  Market Summary
                </h3>
                <span className="text-[10px] text-slate-500 ml-auto" data-id="element-2466">
                  2m ago
                </span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed" data-id="element-2467">
                <strong className="text-white" data-id="element-2468">Wind in Germany</strong> is
                deviating significantly below forecast (−2.1 GW) due to lower
                wind speeds in the North Sea.{' '}
                <strong className="text-white" data-id="element-2469">Solar output</strong> remains
                stable and tracking EPT-2 predictions.{' '}
                <strong className="text-white" data-id="element-2470">Demand</strong> is slightly
                elevated (+1.5%) driven by lower temperatures in Southern
                Germany. Expect increased spot volatility between 14:00–16:00
                UTC.
              </p>
            </div>

            {/* Stacked sparklines */}
            <div className="space-y-4" data-id="element-2471">
              <EnergySparkline title="Wind Generation Forecast" icon={Wind} selectedModelRuns={selectedModelRuns} selectedRegion={selectedRegion} data-id="element-2472" />
              <EnergySparkline title="Solar Generation Forecast" icon={Sun} selectedModelRuns={selectedModelRuns} selectedRegion={selectedRegion} data-id="element-2473" />
              <EnergySparkline title="Temperature Forecast" icon={Thermometer} selectedModelRuns={selectedModelRuns} selectedRegion={selectedRegion} data-id="element-2474" />
            </div>
          </div>

          {/* Heatmap */}
          <DeviationHeatmap selectedModelRuns={selectedModelRuns} data-id="element-2475" />
        </div>
      </div>
    </div>;
}
// Model colors matching the rest of the app
const MODEL_HEX_COLORS: Record<string, string> = {
  'EPT-2': '#EC4899',
  'EC AIFS': '#3b82f6',
  'EC IFS': '#06b6d4',
  GFS: '#eab308',
  'ICON-EU': '#a855f7',
  UKMO: '#f97316',
  'EPT-2 Early': '#14b8a6'
};
const FALLBACK_COLORS = ['#06b6d4', '#f59e0b', '#a855f7', '#f97316', '#64748b'];
function getModelColor(name: string, idx: number): string {
  return MODEL_HEX_COLORS[name] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
}
// Seeded random for stable per-model data
function seededRand(seed: number) {
  let s = seed;
  return () => {
    s = s * 16807 % 2147483647;
    return (s - 1) / 2147483646;
  };
}
const EnergySparkline = ({
  title,
  icon: Icon,
  selectedModelRuns,
  selectedRegion
}: {
  title: string;
  icon: any;
  selectedModelRuns: Record<string, string[]>;
  selectedRegion: string;
}) => {
  const [activeTab, setActiveTab] = useState<'forecast' | 'delta'>('forecast');
  // Must be defined before any useMemo that calls it
  const runLabel = (runIso: string) => {
    if (!runIso) return '';
    const d = new Date(runIso);
    return ` (${d.getUTCHours().toString().padStart(2, '0')}z)`;
  };
  // Build one entry per model+run
  const entries = useMemo(() => {
    const result: {
      modelName: string;
      runIso: string;
      key: string;
      color: string;
      runIndex: number; // index within same model's runs
    }[] = [];
    let colorIdx = 0;
    Object.entries(selectedModelRuns).forEach(([modelName, runs]) => {
      if (!runs?.length) return;
      runs.forEach((runIso, runIndex) => {
        result.push({
          modelName,
          runIso,
          key: runIso ? `${modelName}__${runIso}` : modelName,
          color: getModelColor(modelName, colorIdx),
          runIndex
        });
      });
      colorIdx++;
    });
    if (result.length === 0) {
      result.push({
        modelName: 'EPT-2',
        runIso: '',
        key: 'EPT-2',
        color: '#EC4899',
        runIndex: 0
      });
      result.push({
        modelName: 'EC IFS',
        runIso: '',
        key: 'EC IFS',
        color: '#06b6d4',
        runIndex: 0
      });
    }
    return result;
  }, [selectedModelRuns]);
  // Generate stable mock data per entry
  const data = useMemo(() => {
    const hours = 24;
    const now = new Date();
    now.setMinutes(0, 0, 0);
    return Array.from({
      length: hours
    }, (_, i) => {
      const time = new Date(now.getTime() + i * 3600000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const base = 50 + Math.sin(i / 4) * 20;
      const point: Record<string, any> = {
        time
      };
      entries.forEach(e => {
        const seed = e.key.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 137 + i;
        const rand = seededRand(seed);
        point[e.key] = +(base + (rand() - 0.5) * 10).toFixed(1);
      });
      if (i < 6) point['actual'] = +(base + Math.sin(i) * 1.5).toFixed(1);
      return point;
    });
  }, [entries]);
  // Delta data: for each model with multiple runs, compute run[0] - run[1] per hour
  const deltaData = useMemo(() => {
    const modelGroups: Record<string, typeof entries> = {};
    entries.forEach(e => {
      if (!modelGroups[e.modelName]) modelGroups[e.modelName] = [];
      modelGroups[e.modelName].push(e);
    });
    const multiRunModel = Object.entries(modelGroups).find(([, runs]) => runs.length >= 2);
    if (!multiRunModel) return null;
    const [modelName, runs] = multiRunModel;
    const key0 = runs[0].key;
    const key1 = runs[1].key;
    return {
      modelName,
      run0Label: runLabel(runs[0].runIso),
      run1Label: runLabel(runs[1].runIso),
      color: runs[0].color,
      points: data.map(d => ({
        time: d.time,
        delta: typeof d[key0] === 'number' && typeof d[key1] === 'number' ? +(d[key0] - d[key1]).toFixed(2) : 0
      }))
    };
  }, [entries, data]);
  const hasDelta = !!deltaData;
  const toggleLine = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Keep at least one visible
        if (next.size < entries.length - 1) next.add(key);
      }
      return next;
    });
  };
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  return <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex flex-col h-[220px]" data-id="element-2476">
      {/* Header */}
      <div className="flex items-center justify-between mb-2" data-id="element-2477">
        <div className="flex items-center gap-2" data-id="element-2478">
          <Icon size={14} className="text-slate-400" data-id="element-2479" />
          <span className="text-sm font-medium text-slate-200" data-id="element-2480">{title}</span>
          <span className="text-[10px] text-slate-500" data-id="element-2481">· {selectedRegion}</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-2482">
          {/* Tab switcher */}
          <div className="flex bg-slate-800/80 rounded-md p-0.5 border border-white/[0.06]" data-id="element-2483">
            <button onClick={() => setActiveTab('forecast')} className={`px-2.5 py-0.5 text-[10px] font-medium rounded-sm transition-all ${activeTab === 'forecast' ? 'bg-[#EC4899] text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-2484">
              Forecast
            </button>
            <button onClick={() => setActiveTab('delta')} disabled={!hasDelta} className={`px-2.5 py-0.5 text-[10px] font-medium rounded-sm transition-all ${activeTab === 'delta' ? 'bg-[#EC4899] text-white shadow-sm' : hasDelta ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 cursor-not-allowed'}`} data-id="element-2485">
              Δ Delta
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap mb-2" data-id="element-2486">
        {activeTab === 'forecast' ? <>
            {entries.map(e => {
          const isHidden = hiddenKeys.has(e.key);
          return <button key={e.key} onClick={() => toggleLine(e.key)} className={`flex items-center gap-1 transition-opacity ${isHidden ? 'opacity-30 hover:opacity-60' : 'opacity-100 hover:opacity-80'}`} title={isHidden ? `Show ${e.modelName}` : `Hide ${e.modelName}`} data-id="element-2487">
                  <svg width="16" height="6" data-id="element-2488">
                    <line x1="0" y1="3" x2="16" y2="3" stroke={isHidden ? '#475569' : e.color} strokeWidth="2" strokeDasharray={e.runIndex > 0 ? '4 2' : undefined} opacity={e.runIndex > 0 ? 0.7 : 1} data-id="element-2489" />
                  </svg>
                  <span className={`text-[10px] transition-colors ${isHidden ? 'text-slate-600 line-through decoration-slate-600' : 'text-slate-400'}`} data-id="element-2490">
                    {e.modelName}
                    {runLabel(e.runIso)}
                  </span>
                </button>;
        })}
            <div className="flex items-center gap-1" data-id="element-2491">
              <svg width="16" height="6" data-id="element-2492">
                <line x1="0" y1="3" x2="16" y2="3" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="4 2" data-id="element-2493" />
              </svg>
              <span className="text-[10px] text-slate-500" data-id="element-2494">Actual</span>
            </div>
          </> : deltaData ? <span className="text-[10px] text-slate-400" data-id="element-2495">
            <span className="font-medium text-white" data-id="element-2496">
              {deltaData.modelName}
            </span>
            {deltaData.run0Label} <span className="text-slate-500" data-id="element-2497">minus</span>{' '}
            {deltaData.run1Label}
            <span className="ml-2 text-emerald-400" data-id="element-2498">▲ newer ahead</span>
            <span className="ml-1 text-red-400" data-id="element-2499">▼ newer behind</span>
          </span> : null}
      </div>

      {/* Chart */}
      <div className="flex-1 w-full min-h-0" data-id="element-2500">
        <ResponsiveContainer width="100%" height="100%" data-id="element-2501">
          {activeTab === 'forecast' ? <LineChart data={data} margin={{
          top: 4,
          right: 8,
          left: -8,
          bottom: 0
        }} data-id="element-2502">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} data-id="element-2503" />
              <XAxis dataKey="time" tick={{
            fontSize: 9,
            fill: '#64748b'
          }} tickLine={false} axisLine={{
            stroke: 'rgba(148,163,184,0.1)'
          }} interval={3} data-id="element-2504" />
              <YAxis tick={{
            fontSize: 9,
            fill: '#64748b'
          }} tickLine={false} axisLine={false} domain={['auto', 'auto']} width={32} tickFormatter={v => `${v}`} data-id="element-2505" />
              <Tooltip content={({
            active,
            payload,
            label
          }) => {
            if (!active || !payload?.length) return null;
            return <div className="rounded-xl px-3 py-2.5 border border-white/[0.1] shadow-2xl shadow-black/50 min-w-[160px]" style={{
              background: 'rgba(15,23,42,0.95)',
              backdropFilter: 'blur(20px)'
            }} data-id="element-2507">
                      <div className="text-[10px] text-slate-400 mb-2 font-medium font-mono" data-id="element-2508">
                        {label}
                      </div>
                      <div className="space-y-1.5" data-id="element-2509">
                        {payload.map((item: any) => <div key={item.dataKey} className="flex items-center justify-between gap-4" data-id="element-2510">
                            <div className="flex items-center gap-1.5" data-id="element-2511">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                      backgroundColor: item.color
                    }} data-id="element-2512" />
                              <span className="text-[10px] text-slate-300" data-id="element-2513">
                                {item.name}
                              </span>
                            </div>
                            <span className="text-[11px] text-white font-semibold tabular-nums" data-id="element-2514">
                              {typeof item.value === 'number' ? item.value.toFixed(1) : item.value}
                            </span>
                          </div>)}
                      </div>
                    </div>;
          }} data-id="element-2506" />
              {entries.map(e => <Line key={e.key} type="monotone" dataKey={e.key} name={`${e.modelName}${runLabel(e.runIso)}`} stroke={e.color} strokeWidth={e.runIndex === 0 ? 2 : 1.5} strokeDasharray={e.runIndex > 0 ? '6 3' : undefined} strokeOpacity={e.runIndex > 0 ? 0.7 : 1} dot={false} hide={hiddenKeys.has(e.key)} activeDot={{
            r: 4,
            fill: e.color,
            stroke: '#fff',
            strokeWidth: 1.5
          }} data-id="element-2515" />)}
              <Line type="monotone" dataKey="actual" name="Actual" stroke="rgba(255,255,255,0.4)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} activeDot={{
            r: 3,
            fill: '#fff',
            stroke: '#fff',
            strokeWidth: 1
          }} data-id="element-2516" />
            </LineChart> : <AreaChart data={deltaData?.points ?? []} margin={{
          top: 4,
          right: 8,
          left: -8,
          bottom: 0
        }} data-id="element-2517">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} data-id="element-2518" />
              <XAxis dataKey="time" tick={{
            fontSize: 9,
            fill: '#64748b'
          }} tickLine={false} axisLine={{
            stroke: 'rgba(148,163,184,0.1)'
          }} interval={3} data-id="element-2519" />
              <YAxis tick={{
            fontSize: 9,
            fill: '#64748b'
          }} tickLine={false} axisLine={false} domain={['auto', 'auto']} width={32} tickFormatter={v => `${v > 0 ? '+' : ''}${v}`} data-id="element-2520" />
              <Tooltip content={({
            active,
            payload,
            label
          }) => {
            if (!active || !payload?.length) return null;
            const val = payload[0]?.value as number;
            return <div className="rounded-xl px-3 py-2.5 border border-white/[0.1] shadow-2xl shadow-black/50 min-w-[160px]" style={{
              background: 'rgba(15,23,42,0.95)',
              backdropFilter: 'blur(20px)'
            }} data-id="element-2522">
                      <div className="text-[10px] text-slate-400 mb-1.5 font-medium font-mono" data-id="element-2523">
                        {label}
                      </div>
                      <div className="flex items-center justify-between gap-4" data-id="element-2524">
                        <span className="text-[10px] text-slate-300" data-id="element-2525">
                          Δ {deltaData?.modelName}
                        </span>
                        <span className={`text-[11px] font-semibold tabular-nums ${val > 0 ? 'text-emerald-400' : val < 0 ? 'text-red-400' : 'text-slate-400'}`} data-id="element-2526">
                          {val > 0 ? '+' : ''}
                          {typeof val === 'number' ? val.toFixed(2) : val} GW
                        </span>
                      </div>
                      <div className="text-[9px] text-slate-500 mt-1" data-id="element-2527">
                        {deltaData?.run0Label} vs {deltaData?.run1Label}
                      </div>
                    </div>;
          }} data-id="element-2521" />
              <defs data-id="element-2528">
                <linearGradient id={`deltaGrad-${title}`} x1="0" y1="0" x2="0" y2="1" data-id="element-2529">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-2530" />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-2531" />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="delta" stroke={deltaData?.color ?? '#EC4899'} strokeWidth={2} fill={`url(#deltaGrad-${title})`} dot={false} activeDot={{
            r: 4,
            fill: deltaData?.color ?? '#EC4899',
            stroke: '#fff',
            strokeWidth: 1.5
          }} data-id="element-2532" />
            </AreaChart>}
        </ResponsiveContainer>
      </div>
    </div>;
};